import AuthAPI from '../api/auth';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import DashboardAudioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { useImpersonation } from 'dashboard/composables/useImpersonation';

const { isImpersonating } = useImpersonation();

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    const { websocketURL = '' } = window.chatwootConfig || {};
    super(app, pubsubToken, websocketURL);
    this.CancelTyping = [];
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.created': this.onConversationCreated,
      'conversation.status_changed': this.onStatusChange,
      'user:logout': this.onLogout,
      'page:reload': this.onReload,
      'assignee.changed': this.onAssigneeChanged,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.contact_changed': this.onConversationContactChange,
      'presence.update': this.onPresenceUpdate,
      'contact.deleted': this.onContactDelete,
      'contact.updated': this.onContactUpdate,
      'conversation.mentioned': this.onConversationMentioned,
      'notification.created': this.onNotificationCreated,
      'notification.deleted': this.onNotificationDeleted,
      'notification.updated': this.onNotificationUpdated,
      'conversation.read': this.onConversationRead,
      'conversation.updated': this.onConversationUpdated,
      'account.cache_invalidated': this.onCacheInvalidate,
      'copilot.message.created': this.onCopilotMessageCreated,
      'pending_question.created': this.onPendingQuestionCreated,
      'pending_question.resolved': this.onPendingQuestionResolved,
      'evolution.status_changed': this.onEvolutionStatusChanged,
      'slot.restriction_compiled': this.onSlotRestrictionCompiled,
      'slot.response_instruction_compiled': this.onSlotResponseInstructionCompiled,
      'kanban.card_moved': this.onKanbanCardMoved,
      'kanban.card_created': this.onKanbanCardCreated,
      'kanban.card_updated': this.onKanbanCardUpdated,
      'slot_value.completed': this.onSlotValueCompleted,
      'slot_values.reset': this.onSlotValuesReset,
      'slot_values.navigated': this.onSlotValuesNavigated,
      'field_value.updated': this.onFieldValueUpdated,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT);
  };

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_DISCONNECT);
  };

  isAValidEvent = data => {
    return this.app.$store.getters.getCurrentAccountId === data.account_id;
  };

  onMessageUpdated = data => {
    this.app.$store.dispatch('updateMessage', data);
  };

  onPresenceUpdate = data => {
    if (isImpersonating.value) return;
    this.app.$store.dispatch('contacts/updatePresence', data.contacts);
    this.app.$store.dispatch('agents/updatePresence', data.users);
    this.app.$store.dispatch('setCurrentUserAvailability', data.users);
  };

  onConversationContactChange = payload => {
    const { meta = {}, id: conversationId } = payload;
    const { sender } = meta || {};
    if (conversationId) {
      this.app.$store.dispatch('updateConversationContact', {
        conversationId,
        ...sender,
      });
    }
  };

  onAssigneeChanged = payload => {
    const { id } = payload;
    if (id) {
      this.app.$store.dispatch('updateConversation', payload);
    }
    this.fetchConversationStats();
  };

  onConversationCreated = data => {
    this.app.$store.dispatch('addConversation', data);
    this.fetchConversationStats();
    // Emit event for Kanban board to create new card
    emitter.emit(BUS_EVENTS.KANBAN_CONVERSATION_CREATED, {
      conversationId: data.id,
      inboxId: data.inbox_id,
      conversation: data,
    });
  };

  onConversationRead = data => {
    this.app.$store.dispatch('updateConversation', data);
    // Emit event for Kanban board to update unread count
    emitter.emit(BUS_EVENTS.KANBAN_CONVERSATION_UPDATED, {
      conversationId: data.id,
      conversation: data,
    });
  };

  // eslint-disable-next-line class-methods-use-this
  onLogout = () => AuthAPI.logout();

  onMessageCreated = data => {
    const {
      conversation: { last_activity_at: lastActivityAt },
      conversation_id: conversationId,
    } = data;
    DashboardAudioNotificationHelper.onNewMessage(data);
    this.app.$store.dispatch('addMessage', data);
    this.app.$store.dispatch('updateConversationLastActivity', {
      lastActivityAt,
      conversationId,
    });
    // Emit event for Kanban board real-time updates
    emitter.emit(BUS_EVENTS.KANBAN_MESSAGE_RECEIVED, {
      conversationId,
      message: data,
    });
  };

  // eslint-disable-next-line class-methods-use-this
  onReload = () => window.location.reload();

  onStatusChange = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
    // Emit event for Kanban board real-time updates
    emitter.emit(BUS_EVENTS.KANBAN_CONVERSATION_UPDATED, {
      conversationId: data.id,
      conversation: data,
    });
  };

  onConversationUpdated = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onTypingOn = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/create', {
      conversationId,
      user,
    });
    this.initTimer({ conversation, user });
  };

  onTypingOff = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/destroy', {
      conversationId,
      user,
    });
  };

  onConversationMentioned = data => {
    this.app.$store.dispatch('addMentions', data);
  };

  clearTimer = conversationId => {
    const timerEvent = this.CancelTyping[conversationId];

    if (timerEvent) {
      clearTimeout(timerEvent);
      this.CancelTyping[conversationId] = null;
    }
  };

  initTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    // Turn off typing automatically after 30 seconds
    this.CancelTyping[conversationId] = setTimeout(() => {
      this.onTypingOff({ conversation, user });
    }, 30000);
  };

  // eslint-disable-next-line class-methods-use-this
  fetchConversationStats = () => {
    emitter.emit('fetch_conversation_stats');
  };

  onContactDelete = data => {
    this.app.$store.dispatch(
      'contacts/deleteContactThroughConversations',
      data.id
    );
    this.fetchConversationStats();
  };

  onContactUpdate = data => {
    this.app.$store.dispatch('contacts/updateContact', data);
  };

  onNotificationCreated = data => {
    this.app.$store.dispatch('notifications/addNotification', data);
  };

  onNotificationDeleted = data => {
    this.app.$store.dispatch('notifications/deleteNotification', data);
  };

  onNotificationUpdated = data => {
    this.app.$store.dispatch('notifications/updateNotification', data);
  };

  onCopilotMessageCreated = data => {
    this.app.$store.dispatch('copilotMessages/upsert', data);
  };

  onCacheInvalidate = data => {
    const keys = data.cache_keys;
    this.app.$store.dispatch('labels/revalidate', { newKey: keys.label });
    this.app.$store.dispatch('inboxes/revalidate', { newKey: keys.inbox });
    this.app.$store.dispatch('teams/revalidate', { newKey: keys.team });
  };

  onPendingQuestionCreated = data => {
    this.app.$store.dispatch(
      'pendingQuestionsCount/setCount',
      data.pending_count
    );
  };

  onPendingQuestionResolved = data => {
    this.app.$store.dispatch(
      'pendingQuestionsCount/setCount',
      data.pending_count
    );
  };

  onEvolutionStatusChanged = data => {
    // Emit event for ViperConnect components to listen
    emitter.emit(BUS_EVENTS.EVOLUTION_STATUS_CHANGED, data);
  };

  onSlotRestrictionCompiled = data => {
    // Emit event for SlotsConfig components to listen
    emitter.emit(BUS_EVENTS.SLOT_RESTRICTION_COMPILED, data);
  };

  onSlotResponseInstructionCompiled = data => {
    // Emit event for SlotsConfig components to listen
    emitter.emit(BUS_EVENTS.SLOT_RESPONSE_INSTRUCTION_COMPILED, data);
  };

  onKanbanCardMoved = data => {
    // Emit event for Kanban board real-time updates
    emitter.emit(BUS_EVENTS.KANBAN_CARD_MOVED, data);
  };

  onKanbanCardCreated = data => {
    // Directly dispatch to kanban store - no API call needed
    // The card data is already included in the websocket payload
    this.app.$store.dispatch('kanban/handleCardCreatedFromCable', data);
  };

  onKanbanCardUpdated = data => {
    // Directly dispatch to kanban store - no API call needed
    // The card data is already included in the websocket payload
    this.app.$store.dispatch('kanban/handleCardUpdatedFromCable', data);
  };

  onSlotValueCompleted = data => {
    // Emit event for Kanban card slot badge updates and SlotCard test badge
    emitter.emit(BUS_EVENTS.KANBAN_SLOT_VALUE_COMPLETED, data);
  };

  onSlotValuesReset = data => {
    // Emit event for slot values reset (badges should show pending)
    emitter.emit(BUS_EVENTS.KANBAN_SLOT_VALUES_RESET, data);
  };

  onSlotValuesNavigated = data => {
    emitter.emit(BUS_EVENTS.KANBAN_SLOT_VALUES_NAVIGATED, data);
  };

  onFieldValueUpdated = data => {
    emitter.emit(BUS_EVENTS.KANBAN_FIELD_VALUE_UPDATED, data);
  };
}

export default {
  init(store, pubsubToken) {
    return new ActionCableConnector({ $store: store }, pubsubToken);
  },
};
